import { cva, type VariantProps } from 'class-variance-authority';
import { uiMarker, uiMarkerLayout } from './shared';

/**
 * MASTER–DETAIL
 *
 * The impounds screen: a list on the left, the selected record on the right,
 * and the list able to narrow or slide away to give the record the room.
 * Three widths for the list and two for the detail, each a transition rather
 * than a jump, which is why it is a recipe: the class strings for the five
 * states lived inline in a 850-line page where nobody could see them
 * together.
 */
export const masterDetailRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'relative flex h-screen flex-row items-stretch gap-4',
]);

const pane =
  'flex flex-col rounded-lg border border-border bg-background transition-all duration-slow ease-in-out';

export const masterDetailListRecipe = cva(
  [uiMarker, uiMarkerLayout, pane, 'h-full overflow-hidden'],
  {
    variants: {
      /**
       * `full` when nothing is selected, `narrow` beside an open record,
       * `collapsed` once the reader hides it to read the record wide.
       */
      state: {
        full: 'flex-1 translate-x-0 opacity-100',
        narrow: 'w-96 flex-shrink-0 translate-x-0 opacity-100',
        collapsed: 'w-0 min-w-0 -translate-x-full opacity-0',
      },
    },
    defaultVariants: { state: 'full' },
  }
);

export const masterDetailDetailRecipe = cva(
  [uiMarker, uiMarkerLayout, pane, 'relative overflow-visible'],
  {
    variants: {
      open: {
        true: 'min-w-0 flex-1 translate-x-0 opacity-100',
        false: 'pointer-events-none w-0 min-w-0 translate-x-full opacity-0',
      },
    },
    defaultVariants: { open: false },
  }
);

export type MasterDetailListRecipeProps = VariantProps<
  typeof masterDetailListRecipe
>;
